/**
 * `new URL()` で相対パスを検証する際に使用するダミーオリジン。
 * 実際のホスト名とは無関係な末尾 `.invalid` ドメインを使い、安全側で固定します。
 */
const REDIRECT_PATH_VALIDATION_BASE_ORIGIN =
  "https://redirect-path-validator.invalid";
const FORBIDDEN_REDIRECT_PREFIXES = ["/logout", "/login", "/login/callback"];

/**
 * 許可しないリダイレクト先かどうかを判定します。
 *
 * @param pathname 解析済みのパス名。
 */
const isForbiddenRedirectPath = (pathname: string): boolean => {
  const maybeLocaleStripped = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "");
  return FORBIDDEN_REDIRECT_PREFIXES.some(
    (prefix) =>
      pathname.startsWith(prefix) || maybeLocaleStripped.startsWith(prefix),
  );
};

/**
 * リダイレクト候補のパス文字列を検証し、有効な場合のみ正規化後の値を返します。
 *
 * - 絶対URLやスキーム（`http://` など）が含まれる場合は拒否します。
 * - `//evil.com` のようなプロトコル相対指定も拒否します。
 * - 改行などの制御文字が含まれる場合は拒否します。
 *
 * @param value 検証したいパス文字列。
 * @returns 同一オリジン内で利用可能なパス（クエリ・ハッシュ込み）。不正な場合は `null`。
 */
export const sanitizeRedirectPath = (
  value: string | null | undefined,
): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith("/")) {
    return null;
  }
  if (trimmed.startsWith("//")) {
    return null;
  }
  if (/[\r\n\t\u0000]/u.test(trimmed)) {
    return null;
  }

  try {
    // ベースURLを固定して絶対URLへの昇格を検知する
    const parsed = new URL(trimmed, REDIRECT_PATH_VALIDATION_BASE_ORIGIN);
    if (parsed.origin !== REDIRECT_PATH_VALIDATION_BASE_ORIGIN) {
      return null;
    }
    const combined = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    if (isForbiddenRedirectPath(parsed.pathname)) {
      return null;
    }

    // pathname + search + hash のみを戻し、ホスト情報は落とす
    return combined;
  } catch {
    return null;
  }
};
